import type { Feature, FeatureCollection, Point, Position } from 'geojson';
import {
  DataDrivenPropertyValueSpecification,
  FilterSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
  MapLayerMouseEvent,
  MapMouseEvent,
  Marker,
  SymbolLayerSpecification,
} from 'maplibre-gl';

import {
  createDeviceMarkerElement,
  DEVICE_MARKER_HEIGHT,
  DEVICE_MARKER_IMAGE,
  DEVICE_MARKER_WIDTH,
} from './device-marker.util';

export type MapDeviceMarkerClusterItem<T> = {
  id: string;
  longitude: number;
  latitude: number;
  data: T;
};

export type MapDeviceMarkerSelectionPosition = {
  longitude: number;
  latitude: number;
  clustered: boolean;
};

type DeviceMarkerProperties = {
  deviceId: string;
};

type DeviceMarkerFeature = Feature<Point, DeviceMarkerProperties>;
type DeviceMarkerFeatureCollection = FeatureCollection<
  Point,
  DeviceMarkerProperties
>;

export type MapDeviceMarkerClusterOptions<T> = {
  idPrefix: string;
  foreground?: boolean;
  onPointClick?: (item: MapDeviceMarkerClusterItem<T>) => void;
  onPointMouseEnter?: (item: MapDeviceMarkerClusterItem<T>) => void;
  onPointMouseLeave?: () => void;
  onSelectionPositionChanged?: (
    position: MapDeviceMarkerSelectionPosition | undefined,
  ) => void;
};

const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_BADGE_SIZE = 24;
const CLUSTER_BADGE_PIXEL_RATIO = 2;
const PIN_SHADOW_WIDTH = 24;
const PIN_SHADOW_HEIGHT = 12;
const PIN_SHADOW_PIXEL_RATIO = 2;
const DEFAULT_PIN_SIZE = 1;
const SELECTED_PIN_SIZE = 1.5;
const PIN_WIDTH = DEVICE_MARKER_WIDTH;
const PIN_HEIGHT = DEVICE_MARKER_HEIGHT;
// 端末の底辺を座標に合わせ、DOMマーカーと同じ位置に表示する。
const PIN_TRANSLATE: [number, number] = [0, 0];
const PIN_SHADOW_TRANSLATE: [number, number] = [0, 0];
const SELECTED_PIN_SHADOW_TRANSLATE: [number, number] = [0, 0];
const BADGE_OFFSET: [number, number] = [13, -35];
const BADGE_TEXT_OFFSET: [number, number] = [13, -36];
const SELECTED_BADGE_OFFSET: [number, number] = [20, -55];
const SELECTED_BADGE_TEXT_OFFSET: [number, number] = [20, -56];
const BADGE_TEXT_SIZE = 16;
const SELECTED_BADGE_TEXT_SIZE = 24;

const createEmptyFeatureCollection = (): DeviceMarkerFeatureCollection => ({
  type: 'FeatureCollection',
  features: [],
});

/** MapLibreのsourceとlayerを使って現在位置のデバイスをクラスター表示する。 */
export class MapDeviceMarkerCluster<T> {
  private readonly sourceId: string;
  private readonly clusterHitLayerId: string;
  private readonly shadowLayerId: string;
  private readonly selectedShadowLayerId: string;
  private readonly clusterLayerId: string;
  private readonly selectedClusterLayerId: string;
  private readonly badgeLayerId: string;
  private readonly selectedBadgeLayerId: string;
  private readonly pointLayerId: string;
  private readonly selectedPointLayerId: string;
  private readonly pinImageId: string;
  private readonly shadowImageId: string;
  private readonly badgeImageId: string;
  private readonly itemById = new Map<string, MapDeviceMarkerClusterItem<T>>();
  private selectedId: string | undefined;
  private selectedClusterId: number | undefined;
  // 非同期の選択位置計算で、後から開始した更新だけを表示へ反映するための世代番号。
  private selectionGeneration = 0;
  // 連続した地図更新で、古いidleイベントによる前面マーカーの再描画を防ぐための世代番号。
  private foregroundRenderGeneration = 0;
  private foregroundMarkers: Marker[] = [];
  private initialized = false;

  private readonly pointClickHandler = (event: MapLayerMouseEvent): void => {
    const item = this.getItemFromEvent(event);
    if (item) {
      this.options.onPointClick?.(item);
    }
  };

  private readonly pointMouseEnterHandler = (
    event: MapLayerMouseEvent,
  ): void => {
    this.map.getCanvas().style.cursor = 'pointer';
    const item = this.getItemFromEvent(event);
    if (item) {
      this.options.onPointMouseEnter?.(item);
    }
  };

  private readonly pointMouseLeaveHandler = (): void => {
    this.map.getCanvas().style.cursor = '';
    this.options.onPointMouseLeave?.();
  };

  private readonly clusterClickHandler = (event: MapMouseEvent): void => {
    // 画像・件数バッジも判定し、重なったレイヤーから同じ集合を複数回展開しない。
    const feature = this.map.queryRenderedFeatures(event.point, {
      layers: this.getClusterHitLayerIds(),
    })[0];
    const clusterId = feature?.properties?.['cluster_id'];
    if (typeof clusterId !== 'number' || feature?.geometry.type !== 'Point') {
      return;
    }

    const source = this.map.getSource(this.sourceId) as GeoJSONSource;
    const coordinates: [number, number] = [
      feature.geometry.coordinates[0],
      feature.geometry.coordinates[1],
    ];
    this.expandCluster(source, clusterId, coordinates);
  };

  private readonly zoomEndHandler = (): void => {
    void this.refreshSelectionPosition();
    this.refreshForegroundMarkers();
  };

  private readonly moveEndHandler = (): void => {
    this.refreshForegroundMarkers();
  };

  constructor(
    private readonly map: MapLibreMap,
    private readonly options: MapDeviceMarkerClusterOptions<T>,
  ) {
    const prefix = options.idPrefix;
    this.sourceId = `${prefix}-source`;
    this.clusterHitLayerId = `${prefix}-cluster-hit`;
    this.shadowLayerId = `${prefix}-shadow`;
    this.selectedShadowLayerId = `${prefix}-selected-shadow`;
    this.clusterLayerId = `${prefix}-cluster`;
    this.selectedClusterLayerId = `${prefix}-selected-cluster`;
    this.badgeLayerId = `${prefix}-cluster-badge`;
    this.selectedBadgeLayerId = `${prefix}-selected-cluster-badge`;
    this.pointLayerId = `${prefix}-point`;
    this.selectedPointLayerId = `${prefix}-selected-point`;
    this.pinImageId = `${prefix}-pin`;
    this.shadowImageId = `${prefix}-shadow-image`;
    this.badgeImageId = `${prefix}-cluster-badge-image`;
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.loadPinImage();
    this.addShadowImage();
    this.addBadgeImage();
    this.addSourceAndLayers();
    this.bindEvents();
    this.initialized = true;
  }

  setItems(items: MapDeviceMarkerClusterItem<T>[]): void {
    this.itemById.clear();
    const features = items.map((item) => {
      this.itemById.set(item.id, item);
      return this.toFeature(item);
    });
    this.getSource()?.setData({ type: 'FeatureCollection', features });
    this.scheduleForegroundMarkerRefresh();
    void this.refreshSelectionPosition();
  }

  async setSelectedId(selectedId?: string): Promise<void> {
    this.selectedId = selectedId;
    this.updatePointSize();
    if (selectedId === undefined) {
      // 選択解除時は再計算を待たず、クラスターの強調表示も解除する。
      this.selectedClusterId = undefined;
      this.updateClusterSize();
    }
    await this.refreshSelectionPosition();
  }

  isFeatureAt(point: MapMouseEvent['point']): boolean {
    if (!this.initialized || this.options.foreground) {
      return false;
    }
    return (
      this.map.queryRenderedFeatures(point, {
        layers: [
          ...this.getClusterHitLayerIds(),
          this.pointLayerId,
        ],
      }).length > 0
    );
  }

  destroy(): void {
    if (!this.initialized) {
      return;
    }

    this.unbindEvents();
    this.clearForegroundMarkers();
    for (const layerId of this.getLayerIds().reverse()) {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    }
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }
    for (const imageId of [
      this.pinImageId,
      this.shadowImageId,
      this.badgeImageId,
    ]) {
      if (this.map.hasImage(imageId)) {
        this.map.removeImage(imageId);
      }
    }
    this.itemById.clear();
    this.initialized = false;
  }

  private addSourceAndLayers(): void {
    // クラスター計算はGeoJSON sourceへ集約し、通常表示と選択表示はfilterで描き分ける。
    this.map.addSource(this.sourceId, {
      type: 'geojson',
      data: createEmptyFeatureCollection(),
      cluster: true,
      clusterRadius: CLUSTER_RADIUS,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
    });
    this.map.addLayer({
      id: this.clusterHitLayerId,
      type: 'circle',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      // ピン画像より広い透明レイヤーをクリック判定に使い、クラスターを操作しやすくする。
      paint: { 'circle-radius': 32, 'circle-opacity': 0 },
    });
    this.map.addLayer({
      id: this.shadowLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.shadowImageId,
        'icon-size': DEFAULT_PIN_SIZE,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': PIN_SHADOW_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.selectedShadowLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: this.getSelectedFeatureFilter(),
      layout: {
        'icon-image': this.shadowImageId,
        'icon-size': SELECTED_PIN_SIZE,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': SELECTED_PIN_SHADOW_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.clusterLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      layout: {
        ...this.createPinLayout(DEFAULT_PIN_SIZE),
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': PIN_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.pointLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: ['!', ['has', 'point_count']],
      layout: {
        ...this.createPinLayout(DEFAULT_PIN_SIZE),
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': PIN_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.badgeLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: this.getBadgeFilter(),
      layout: {
        'icon-image': this.badgeImageId,
        'icon-size': DEFAULT_PIN_SIZE,
        'icon-text-fit': 'both',
        'icon-text-fit-padding': [2, 4, 2, 4],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': this.getMapTextFont(),
        'text-size': BADGE_TEXT_SIZE,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': BADGE_OFFSET,
        'icon-translate-anchor': 'viewport',
        'text-color': '#ffffff',
        'text-translate': BADGE_TEXT_OFFSET,
        'text-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.selectedClusterLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: this.getSelectedClusterFilter(),
      layout: {
        ...this.createPinLayout(SELECTED_PIN_SIZE),
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': PIN_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.selectedPointLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: this.getSelectedPointFilter(),
      layout: {
        ...this.createPinLayout(SELECTED_PIN_SIZE),
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': PIN_TRANSLATE,
        'icon-translate-anchor': 'viewport',
      },
    });
    this.map.addLayer({
      id: this.selectedBadgeLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: this.getBadgeFilter(true),
      layout: {
        'icon-image': this.badgeImageId,
        'icon-size': SELECTED_PIN_SIZE,
        'icon-text-fit': 'both',
        'icon-text-fit-padding': [2, 4, 2, 4],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': this.getMapTextFont(),
        'text-size': SELECTED_BADGE_TEXT_SIZE,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        visibility: this.options.foreground ? 'none' : 'visible',
      },
      paint: {
        'icon-translate': SELECTED_BADGE_OFFSET,
        'icon-translate-anchor': 'viewport',
        'text-color': '#ffffff',
        'text-translate': SELECTED_BADGE_TEXT_OFFSET,
        'text-translate-anchor': 'viewport',
      },
    });
  }

  private bindEvents(): void {
    if (this.options.foreground) {
      this.map.on('moveend', this.moveEndHandler);
      this.map.on('zoomend', this.zoomEndHandler);
      return;
    }
    this.map.on('click', this.pointLayerId, this.pointClickHandler);
    this.map.on('mouseenter', this.pointLayerId, this.pointMouseEnterHandler);
    this.map.on('mouseleave', this.pointLayerId, this.pointMouseLeaveHandler);
    this.map.on('click', this.clusterClickHandler);
    this.map.on('zoomend', this.zoomEndHandler);
  }

  private unbindEvents(): void {
    if (this.options.foreground) {
      this.map.off('moveend', this.moveEndHandler);
      this.map.off('zoomend', this.zoomEndHandler);
      return;
    }
    this.map.off('click', this.pointLayerId, this.pointClickHandler);
    this.map.off('mouseenter', this.pointLayerId, this.pointMouseEnterHandler);
    this.map.off('mouseleave', this.pointLayerId, this.pointMouseLeaveHandler);
    this.map.off('click', this.clusterClickHandler);
    this.map.off('zoomend', this.zoomEndHandler);
  }

  private async refreshSelectionPosition(): Promise<void> {
    const generation = ++this.selectionGeneration;

    const selectedItem = this.selectedId
      ? this.itemById.get(this.selectedId)
      : undefined;
    if (!selectedItem || !this.initialized) {
      this.options.onSelectionPositionChanged?.(undefined);
      return;
    }

    if (!this.map.isSourceLoaded(this.sourceId)) {
      // クラスター所属は現在のズームに依存するため、sourceの再計算完了を待つ。
      await new Promise<void>((resolve) =>
        this.map.once('idle', () => resolve()),
      );
    }
    if (generation !== this.selectionGeneration) {
      return;
    }

    const cluster = await this.findClusterContaining(selectedItem.id);
    if (generation !== this.selectionGeneration) {
      return;
    }
    // sourceの再計算中も直前の選択表示を維持し、最新の計算結果が確定してから更新する。
    // これにより通常サイズのピンが一瞬露出することを防ぐ。
    this.selectedClusterId = cluster?.id;
    this.updateClusterSize();
    this.options.onSelectionPositionChanged?.(
      cluster
        ? {
            longitude: cluster.coordinates[0],
            latitude: cluster.coordinates[1],
            clustered: true,
          }
        : {
            longitude: selectedItem.longitude,
            latitude: selectedItem.latitude,
            clustered: false,
          },
    );
  }

  private async findClusterContaining(
    selectedId: string,
  ): Promise<{ id: number; coordinates: Position } | undefined> {
    const source = this.getSource();
    if (!source) {
      return undefined;
    }
    const processedIds = new Set<number>();
    // querySourceFeaturesはタイル境界で同じクラスターを返す場合があるため、ID単位で一度だけ調べる。
    for (const feature of this.map.querySourceFeatures(this.sourceId)) {
      const clusterId = feature.properties?.['cluster_id'];
      const pointCount = feature.properties?.['point_count'];
      if (
        typeof clusterId !== 'number' ||
        typeof pointCount !== 'number' ||
        processedIds.has(clusterId) ||
        feature.geometry.type !== 'Point'
      ) {
        continue;
      }
      processedIds.add(clusterId);
      const leaves = await this.getClusterLeaves(source, clusterId, pointCount);
      if (leaves.some((leaf) => leaf.properties?.['deviceId'] === selectedId)) {
        return { id: clusterId, coordinates: feature.geometry.coordinates };
      }
    }
    return undefined;
  }

  private getClusterLeaves(
    source: GeoJSONSource,
    clusterId: number,
    pointCount: number,
  ): Promise<Feature[]> {
    return new Promise<Feature[]>((resolve) => {
      source.getClusterLeaves(clusterId, pointCount, 0, (error, features) => {
        resolve(error ? [] : (features ?? []));
      });
    });
  }

  private updatePointSize(): void {
    if (this.map.getLayer(this.selectedPointLayerId)) {
      this.map.setFilter(
        this.selectedPointLayerId,
        this.getSelectedPointFilter(),
      );
    }
    this.updateSelectedShadowFilter();
  }

  private updateClusterSize(): void {
    if (this.map.getLayer(this.selectedClusterLayerId)) {
      this.map.setFilter(
        this.selectedClusterLayerId,
        this.getSelectedClusterFilter(),
      );
    }
    if (this.map.getLayer(this.badgeLayerId)) {
      this.map.setFilter(this.badgeLayerId, this.getBadgeFilter());
    }
    if (this.map.getLayer(this.selectedBadgeLayerId)) {
      this.map.setFilter(this.selectedBadgeLayerId, this.getBadgeFilter(true));
    }
    this.updateSelectedShadowFilter();
  }

  private updateSelectedShadowFilter(): void {
    if (!this.map.getLayer(this.selectedShadowLayerId)) {
      return;
    }
    this.map.setFilter(
      this.selectedShadowLayerId,
      this.getSelectedFeatureFilter(),
    );
  }

  private getSelectedPointFilter(): FilterSpecification {
    return this.selectedId
      ? ['==', ['get', 'deviceId'], this.selectedId]
      : ['==', ['get', 'deviceId'], ''];
  }

  private getSelectedClusterFilter(): FilterSpecification {
    return this.selectedClusterId === undefined
      ? ['==', ['get', 'cluster_id'], -1]
      : ['==', ['get', 'cluster_id'], this.selectedClusterId];
  }

  private getSelectedFeatureFilter(): FilterSpecification {
    // 選択対象がクラスター内ならクラスターを、単独表示ならデバイス自身を強調する。
    return this.selectedClusterId === undefined
      ? this.getSelectedPointFilter()
      : this.getSelectedClusterFilter();
  }

  private getBadgeFilter(selected = false): FilterSpecification {
    if (this.selectedClusterId === undefined) {
      return selected
        ? ['==', ['get', 'cluster_id'], -1]
        : ['has', 'point_count'];
    }
    return [
      'all',
      ['has', 'point_count'],
      [selected ? '==' : '!=', ['get', 'cluster_id'], this.selectedClusterId],
    ];
  }

  private createPinLayout(
    iconSize: DataDrivenPropertyValueSpecification<number>,
  ): NonNullable<SymbolLayerSpecification['layout']> {
    return {
      'icon-image': this.pinImageId,
      'icon-size': iconSize,
      'icon-anchor': 'bottom' as const,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    };
  }

  private toFeature(item: MapDeviceMarkerClusterItem<T>): DeviceMarkerFeature {
    return {
      type: 'Feature',
      id: item.id,
      geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
      properties: { deviceId: item.id },
    };
  }

  private getItemFromEvent(
    event: MapLayerMouseEvent,
  ): MapDeviceMarkerClusterItem<T> | undefined {
    const id = event.features?.[0]?.properties?.['deviceId'];
    return typeof id === 'string' ? this.itemById.get(id) : undefined;
  }

  private getSource(): GeoJSONSource | undefined {
    return this.map.getSource(this.sourceId) as GeoJSONSource | undefined;
  }

  private getClusterHitLayerIds(): string[] {
    return [
      this.clusterHitLayerId,
      this.clusterLayerId,
      this.selectedClusterLayerId,
      this.badgeLayerId,
      this.selectedBadgeLayerId,
    ];
  }

  private getLayerIds(): string[] {
    return [
      this.clusterHitLayerId,
      this.shadowLayerId,
      this.selectedShadowLayerId,
      this.clusterLayerId,
      this.pointLayerId,
      this.badgeLayerId,
      this.selectedClusterLayerId,
      this.selectedPointLayerId,
      this.selectedBadgeLayerId,
    ];
  }

  private scheduleForegroundMarkerRefresh(): void {
    if (!this.options.foreground) {
      return;
    }
    const generation = ++this.foregroundRenderGeneration;
    // source更新直後はクラスター結果が未確定のため、idle後にHTML Markerへ同期する。
    this.map.once('idle', () => {
      if (generation === this.foregroundRenderGeneration) {
        this.refreshForegroundMarkers();
      }
    });
  }

  private refreshForegroundMarkers(): void {
    if (!this.initialized || !this.options.foreground) {
      return;
    }
    this.clearForegroundMarkers();
    const renderedKeys = new Set<string>();
    // querySourceFeaturesの重複結果から同じHTML Markerを複数生成しないようにする。
    for (const feature of this.map.querySourceFeatures(this.sourceId)) {
      if (feature.geometry.type !== 'Point') {
        continue;
      }
      const coordinates = feature.geometry.coordinates;
      const longitude = coordinates[0];
      const latitude = coordinates[1];
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        continue;
      }

      const clusterId = feature.properties?.['cluster_id'];
      const deviceId = feature.properties?.['deviceId'];
      const markerKey =
        typeof clusterId === 'number'
          ? `cluster:${clusterId}`
          : `device:${String(deviceId)}`;
      if (renderedKeys.has(markerKey)) {
        continue;
      }
      renderedKeys.add(markerKey);
      this.addForegroundMarker(
        [longitude, latitude],
        typeof clusterId === 'number' ? clusterId : undefined,
        typeof deviceId === 'string' ? deviceId : undefined,
        feature.properties?.['point_count_abbreviated'],
      );
    }
  }

  private addForegroundMarker(
    coordinates: [number, number],
    clusterId?: number,
    deviceId?: string,
    pointCount?: string | number,
  ): void {
    const element = this.createForegroundPinElement(pointCount);
    // HTML Marker上の操作を地図本体へ伝播させず、位置情報編集のクリックと分離する。
    element.addEventListener('mousedown', (event) => event.stopPropagation());
    element.addEventListener('click', (event) => {
      event.stopPropagation();
      if (clusterId !== undefined) {
        const source = this.getSource();
        if (source) {
          this.expandCluster(source, clusterId, coordinates);
        }
        return;
      }
      const item = deviceId ? this.itemById.get(deviceId) : undefined;
      if (item) {
        this.options.onPointClick?.(item);
      }
    });

    if (deviceId) {
      element.addEventListener('mouseenter', () => {
        const item = this.itemById.get(deviceId);
        if (item) {
          this.options.onPointMouseEnter?.(item);
        }
      });
      element.addEventListener('mouseleave', () => {
        this.options.onPointMouseLeave?.();
      });
    }

    this.foregroundMarkers.push(
      new Marker({ element, anchor: 'bottom' })
        .setLngLat(coordinates)
        .addTo(this.map),
    );
  }

  private createForegroundPinElement(
    pointCount?: string | number,
  ): HTMLElement {
    const element = createDeviceMarkerElement();
    element.style.zIndex = '2';
    element.prepend(this.createForegroundShadow());

    if (pointCount !== undefined) {
      element.appendChild(this.createForegroundBadge(String(pointCount)));
    }
    return element;
  }

  private createForegroundBadge(pointCount: string): HTMLElement {
    const badge = document.createElement('span');
    badge.className = 'device-pin-badge';
    badge.textContent = pointCount;
    badge.style.position = 'absolute';
    badge.style.left = '15px';
    badge.style.top = '-12px';
    badge.style.minWidth = `${CLUSTER_BADGE_SIZE}px`;
    badge.style.height = `${CLUSTER_BADGE_SIZE}px`;
    badge.style.padding = '0 4px';
    badge.style.boxSizing = 'border-box';
    badge.style.border = '2px solid #ffffff';
    badge.style.borderRadius = '9999px';
    badge.style.backgroundColor = '#3f454d';
    badge.style.color = '#ffffff';
    badge.style.display = 'flex';
    badge.style.alignItems = 'center';
    badge.style.justifyContent = 'center';
    badge.style.fontSize = `${BADGE_TEXT_SIZE}px`;
    badge.style.lineHeight = '1';
    badge.style.boxShadow = '1px 1px 2px rgba(0, 0, 0, 0.18)';
    badge.style.zIndex = '2';
    return badge;
  }

  private createForegroundShadow(): HTMLElement {
    const shadow = document.createElement('span');
    shadow.className = 'device-pin-shadow';
    shadow.style.position = 'absolute';
    shadow.style.left = `${(PIN_WIDTH - PIN_SHADOW_WIDTH) / 2}px`;
    shadow.style.top = `${PIN_HEIGHT - PIN_SHADOW_HEIGHT / 2}px`;
    shadow.style.width = `${PIN_SHADOW_WIDTH}px`;
    shadow.style.height = `${PIN_SHADOW_HEIGHT}px`;
    shadow.style.borderRadius = '50%';
    shadow.style.background =
      'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.12) 48%, rgba(0, 0, 0, 0) 75%)';
    shadow.style.pointerEvents = 'none';
    return shadow;
  }

  private expandCluster(
    source: GeoJSONSource,
    clusterId: number,
    coordinates: [number, number],
  ): void {
    source.getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (!error && zoom != null) {
        this.map.easeTo({ center: coordinates, zoom });
      }
    });
  }

  private clearForegroundMarkers(): void {
    this.foregroundMarkers.forEach((marker) => marker.remove());
    this.foregroundMarkers = [];
  }

  private async loadPinImage(): Promise<void> {
    if (this.map.hasImage(this.pinImageId)) {
      return;
    }
    const imageUrl = new URL(DEVICE_MARKER_IMAGE, document.baseURI);
    await new Promise<void>((resolve, reject) => {
      this.map.loadImage(imageUrl.toString(), (error, image) => {
        if (error || !image) {
          reject(
            error ??
              new Error('デバイス位置のピン画像を読み込めませんでした。'),
          );
          return;
        }
        this.map.addImage(this.pinImageId, image);
        resolve();
      });
    });
  }

  private addBadgeImage(): void {
    if (this.map.hasImage(this.badgeImageId)) {
      return;
    }
    const size = CLUSTER_BADGE_SIZE * CLUSTER_BADGE_PIXEL_RATIO;
    const center = size / 2;
    const borderWidth = 2 * CLUSTER_BADGE_PIXEL_RATIO;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('クラスター件数バッジを生成できませんでした。');
    }
    context.beginPath();
    context.arc(center, center, center, 0, Math.PI * 2);
    context.fillStyle = '#ffffff';
    context.fill();
    context.beginPath();
    context.arc(center, center, center - borderWidth, 0, Math.PI * 2);
    context.fillStyle = '#3f454d';
    context.fill();
    // 件数の桁数が増えても中央部分だけを伸ばし、円形の縁を崩さない。
    this.map.addImage(
      this.badgeImageId,
      context.getImageData(0, 0, size, size),
      {
        pixelRatio: CLUSTER_BADGE_PIXEL_RATIO,
        stretchX: [[center - 1, center + 1]],
        stretchY: [[center - 1, center + 1]],
        content: [
          borderWidth,
          borderWidth,
          size - borderWidth,
          size - borderWidth,
        ],
      },
    );
  }

  private addShadowImage(): void {
    if (this.map.hasImage(this.shadowImageId)) {
      return;
    }
    const width = PIN_SHADOW_WIDTH * PIN_SHADOW_PIXEL_RATIO;
    const height = PIN_SHADOW_HEIGHT * PIN_SHADOW_PIXEL_RATIO;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('デバイス位置のピン影を生成できませんでした。');
    }

    context.scale(PIN_SHADOW_PIXEL_RATIO, PIN_SHADOW_PIXEL_RATIO);
    context.fillStyle = 'rgba(0, 0, 0, 0.04)';
    for (const [radiusX, radiusY] of [
      [10.5, 5.25],
      [10.5, 5.25],
      [9.5, 4.77],
      [8.5, 4.3],
      [7.5, 3.82],
      [6.5, 3.34],
      [5.5, 2.86],
      [4.5, 2.39],
    ]) {
      context.beginPath();
      context.ellipse(
        PIN_SHADOW_WIDTH / 2,
        PIN_SHADOW_HEIGHT / 2,
        radiusX,
        radiusY,
        0,
        0,
        Math.PI * 2,
      );
      context.fill();
    }
    this.map.addImage(
      this.shadowImageId,
      context.getImageData(0, 0, width, height),
      { pixelRatio: PIN_SHADOW_PIXEL_RATIO },
    );
  }

  private getMapTextFont(): string[] {
    // styleに存在するフォントを流用し、未登録フォント指定によるバッジ描画エラーを避ける。
    for (const layer of this.map.getStyle().layers ?? []) {
      if (layer.type !== 'symbol') {
        continue;
      }
      const font = layer.layout?.['text-font'];
      if (
        Array.isArray(font) &&
        font.every((item) => typeof item === 'string')
      ) {
        return [...font] as string[];
      }
    }
    return ['Arial Regular'];
  }
}
